import { ExternalTokenizer } from "lezer";
import { whitespace, LineComment, BlockComment, String, Number, Bool, Null, ParenL, ParenR, BraceL, BraceR, BracketL, BracketR, Semi, Dot, Operator, Punctuation, SpecialVar, Identifier, QuotedIdentifier, Keyword, Type, Builtin } from "./sql.grammar.terms";
function isAlpha(ch) {
    return ch >= 65 /* A */ && ch <= 90 /* Z */ || ch >= 97 /* a */ && ch <= 122 /* z */ || ch >= 48 /* _0 */ && ch <= 57 /* _9 */;
}
function isHexDigit(ch) {
    return ch >= 48 /* _0 */ && ch <= 57 /* _9 */ || ch >= 97 /* a */ && ch <= 102 /* f */ || ch >= 65 /* A */ && ch <= 70 /* F */;
}
function readLiteral(input, pos, endQuote, backslashEscapes) {
    for (let escaped = false;;) {
        let next = input.get(pos++);
        if (next < 0)
            return pos - 1;
        if (next == endQuote && !escaped)
            return pos;
        escaped = backslashEscapes && !escaped && next == 92 /* Backslash */;
    }
}
function readWord(input, pos) {
    for (;; pos++) {
        let next = input.get(pos);
        if (next != 95 /* Underscore */ && !isAlpha(next))
            break;
    }
    return pos;
}
function readWordOrQuoted(input, pos) {
    let next = input.get(pos);
    if (next == 39 /* SingleQuote */ || next == 34 /* DoubleQuote */ || next == 96 /* Backtick */)
        return readLiteral(input, pos + 1, next, false);
    return readWord(input, pos);
}
function readNumber(input, pos, sawDot) {
    let next;
    for (;; pos++) {
        next = input.get(pos);
        if (next == 46 /* Dot */) {
            if (sawDot)
                break;
            sawDot = true;
        }
        else if (next < 48 /* _0 */ || next > 57 /* _9 */) {
            break;
        }
    }
    if (next == 69 /* E */ || next == 101 /* e */) {
        next = input.get(++pos);
        if (next == 43 /* Plus */ || next == 45 /* Dash */)
            pos++;
        for (;; pos++) {
            next = input.get(pos);
            if (next < 48 /* _0 */ || next > 57 /* _9 */)
                break;
        }
    }
    return pos;
}
function eol(input, pos) {
    for (;; pos++) {
        let next = input.get(pos);
        if (next < 0 || next == 10 /* Newline */)
            return pos;
    }
}
function inString(ch, str) {
    for (let i = 0; i < str.length; i++)
        if (str.charCodeAt(i) == ch)
            return true;
    return false;
}
const Space = " \t\r\n";
function keywords(keywords, types, builtin) {
    let result = Object.create(null);
    result["true"] = result["false"] = Bool;
    result["null"] = result["unknown"] = Null;
    for (let kw of keywords.split(" "))
        if (kw)
            result[kw] = Keyword;
    for (let tp of types.split(" "))
        if (tp)
            result[tp] = Type;
    for (let kw of (builtin || "").split(" "))
        if (kw)
            result[kw] = Builtin;
    return result;
}
export const SQLTypes = "array binary bit boolean char character clob date decimal double float int integer interval large national nchar nclob numeric object precision real smallint time timestamp varchar varying ";
export const SQLKeywords = "absolute action add after all allocate alter and any are as asc assertion at authorization before begin between blob both breadth by call cascade cascaded case cast catalog check close collate collation column commit condition connect connection constraint constraints constructor continue corresponding count create cross cube current current_date current_default_transform_group current_transform_group_for_type current_path current_role current_time current_timestamp current_user cursor cycle data day deallocate dec declare default deferrable deferred delete depth deref desc describe descriptor deterministic diagnostics disconnect distinct do domain drop dynamic each else elseif end end-exec equals escape except exception exec execute exists exit external fetch first for foreign found from free full function general get global go goto grant group grouping handle having hold hour identity if immediate in indicator initially inner inout input insert intersect into is isolation join key language last lateral leading leave left level like limit local localtime localtimestamp locator loop map match method minute modifies module month names natural nesting new next no none not of old on only open option or order ordinality out outer output overlaps pad parameter partial path prepare preserve primary prior privileges procedure public read reads recursive redo ref references referencing relative release repeat resignal restrict result return returns revoke right role rollback rollup routine row rows savepoint schema scroll search second section select session session_user set sets signal similar size some space specific specifictype sql sqlexception sqlstate sqlwarning start state static system_user table temporary then timezone_hour timezone_minute to trailing transaction translation treat trigger under undo union unique unnest until update usage user using value values view when whenever where while with without work write year zone ";
const defaults = {
    backslashEscapes: false,
    hashComments: false,
    spaceAfterDashes: false,
    slashComments: false,
    doubleQuotedStrings: false,
    charSetCasts: false,
    operatorChars: "*+\-%<>!=&|~^/",
    specialVar: "?",
    identifierQuotes: '"',
    words: keywords(SQLKeywords, SQLTypes)
};
export function dialect(spec, kws, types, builtin) {
    let dialect = {};
    for (let prop in defaults)
        dialect[prop] = (spec.hasOwnProperty(prop) ? spec : defaults)[prop];
    if (kws)
        dialect.words = keywords(kws, types || "", builtin);
    return dialect;
}
export function tokensFor(d) {
    return new ExternalTokenizer((input, token) => {
        var _a;
        let pos = token.start, next = input.get(pos++), next2 = input.get(pos);
        if (inString(next, Space)) {
            while (inString(input.get(pos), Space))
                pos++;
            token.accept(whitespace, pos);
        }
        else if (next == 39 /* SingleQuote */ || next == 34 /* DoubleQuote */ && d.doubleQuotedStrings) {
            token.accept(String, readLiteral(input, pos, next, d.backslashEscapes));
        }
        else if (next == 35 /* Hash */ && d.hashComments ||
            next == 47 /* Slash */ && next2 == 47 /* Slash */ && d.slashComments) {
            token.accept(LineComment, eol(input, pos));
        }
        else if (next == 45 /* Dash */ && next2 == 45 /* Dash */ &&
            (!d.spaceAfterDashes || input.get(pos + 1) == 32 /* Space */)) {
            token.accept(LineComment, eol(input, pos + 1));
        }
        else if (next == 47 /* Slash */ && next2 == 42 /* Star */) {
            pos++;
            for (let prev = -1, depth = 1;;) {
                let next = input.get(pos++);
                if (next < 0) {
                    pos--;
                    break;
                }
                else if (prev == 42 /* Star */ && next == 47 /* Slash */) {
                    depth--;
                    if (!depth)
                        break;
                    next = -1;
                }
                else if (prev == 47 /* Slash */ && next == 42 /* Star */) {
                    depth++;
                    next = -1;
                }
                prev = next;
            }
            token.accept(BlockComment, pos);
        }
        else if ((next == 101 /* e */ || next == 69 /* E */) && next2 == 39 /* SingleQuote */) {
            token.accept(String, readLiteral(input, pos + 1, 39 /* SingleQuote */, true));
        }
        else if ((next == 110 /* n */ || next == 78 /* N */) && next2 == 39 /* SingleQuote */ &&
            d.charSetCasts) {
            token.accept(String, readLiteral(input, pos + 1, 39 /* SingleQuote */, d.backslashEscapes));
        }
        else if (next == 95 /* Underscore */ && d.charSetCasts) {
            for (;;) {
                let next = input.get(pos++);
                if (next == 39 /* SingleQuote */ && pos > token.start + 2) {
                    token.accept(String, readLiteral(input, pos, 39 /* SingleQuote */, d.backslashEscapes));
                    break;
                }
                if (!isAlpha(next))
                    break;
            }
        }
        else if (next == 40 /* ParenL */) {
            token.accept(ParenL, pos);
        }
        else if (next == 41 /* ParenR */) {
            token.accept(ParenR, pos);
        }
        else if (next == 123 /* BraceL */) {
            token.accept(BraceL, pos);
        }
        else if (next == 125 /* BraceR */) {
            token.accept(BraceR, pos);
        }
        else if (next == 91 /* BracketL */) {
            token.accept(BracketL, pos);
        }
        else if (next == 93 /* BracketR */) {
            token.accept(BracketR, pos);
        }
        else if (next == 59 /* Semi */) {
            token.accept(Semi, pos);
        }
        else if (next == 48 /* _0 */ && (next2 == 98 /* b */ || next2 == 66 /* B */) ||
            (next == 98 /* b */ || next == 66 /* B */) && next2 == 39 /* SingleQuote */) {
            let quoted = next2 == 39 /* SingleQuote */;
            pos++;
            while ((next = input.get(pos)) == 48 /* _0 */ || next == 49 /* _1 */)
                pos++;
            if (quoted && next == 39 /* SingleQuote */)
                pos++;
            token.accept(Number, pos);
        }
        else if (next == 48 /* _0 */ && (next2 == 120 /* x */ || next2 == 88 /* X */) ||
            (next == 120 /* x */ || next == 88 /* X */) && next2 == 39 /* SingleQuote */) {
            let quoted = next2 == 39 /* SingleQuote */;
            pos++;
            while (isHexDigit(next = input.get(pos)))
                pos++;
            if (quoted && next == 39 /* SingleQuote */)
                pos++;
            token.accept(Number, pos);
        }
        else if (next == 46 /* Dot */ && next2 >= 48 /* _0 */ && next2 <= 57 /* _9 */) {
            token.accept(Number, readNumber(input, pos + 1, true));
        }
        else if (next == 46 /* Dot */) {
            token.accept(Dot, pos);
        }
        else if (next >= 48 /* _0 */ && next <= 57 /* _9 */) {
            token.accept(Number, readNumber(input, pos, false));
        }
        else if (inString(next, d.operatorChars)) {
            while (inString(input.get(pos), d.operatorChars))
                pos++;
            token.accept(Operator, pos);
        }
        else if (inString(next, d.specialVar)) {
            token.accept(SpecialVar, readWordOrQuoted(input, next2 == next ? pos + 1 : pos));
        }
        else if (inString(next, d.identifierQuotes)) {
            token.accept(QuotedIdentifier, readLiteral(input, pos + 1, next, false));
        }
        else if (next == 58 /* Colon */ || next == 44 /* Comma */) {
            token.accept(Punctuation, pos);
        }
        else if (isAlpha(next)) {
            pos = readWord(input, pos);
            token.accept((_a = d.words[input.read(token.start, pos).toLowerCase()]) !== null && _a !== void 0 ? _a : Identifier, pos);
        }
    });
}
export const tokens = tokensFor(defaults);
